import type { HotelChainRep } from "@/lib/types/hotel";

/** The default Nowadays organization that holds globally-shared chain reps. Exported so the other
 *  chain-rep readers key off one value rather than each redeclaring it. Lives here, not in
 *  `hotel-chain-reps`, so this pure module has no database import. */
export const GLOBAL_ORG_ID = 999;

/** Whether an enterprise keeps our global chain rep beside its own when it has set nothing. On,
 *  because an enterprise that wants only its own rep for a chain is the exception. Must match the
 *  `enterprise_organizations.include_global_chain_rep` column default. */
export const DEFAULT_INCLUDE_GLOBAL_CHAIN_REP = true;

/** An enterprise's own reps for one venue, and whether it still wants our global chain rep copied. */
export interface EnterpriseRepPreferences {
  /** The enterprise's preferred rep emails for this venue, chain or brand (lowercased, deduped). */
  emails: string[];
  /** Keep the global chain rep on the RFP alongside the enterprise's own reps. Resolved from the
   *  chain row's override, else the enterprise-wide setting. */
  includeGlobalChainRep: boolean;
}

/** An enterprise with no reps configured for the venue: the global chain rep stays either way.
 *  Frozen because every caller shares it as a fallback. */
export const NO_ENTERPRISE_REPS: EnterpriseRepPreferences = Object.freeze({
  emails: Object.freeze([]) as unknown as string[],
  includeGlobalChainRep: DEFAULT_INCLUDE_GLOBAL_CHAIN_REP,
});

/** Chain reps after {@link applyEnterpriseRepPrecedence}, plus who stands in for the dropped ones. */
export interface EnterpriseRepPrecedence<T> {
  /** The chain reps to use: global-org reps removed when the enterprise has its own. */
  chainReps: T[];
  /** The enterprise's rep emails when a global rep was actually dropped for them, else `[]`. A
   *  venue with no email of its own is addressed to these in place of the dropped global rep. */
  replacementRepEmails: string[];
}

/**
 * Drops the global Nowadays chain reps (`org_id === 999`) when the event's enterprise has named
 * its own rep for this venue or chain and opted out of keeping ours, e.g. an enterprise whose
 * GSO handles that account alone. By default ours is copied beside the enterprise's own.
 * Org-specific chain reps are always kept.
 *
 * @param chainReps - The chain reps resolved by `getPreferredChainRep`.
 * @param enterpriseReps - The enterprise's preferred reps for this venue/chain and its opt-in.
 * @returns The chain reps to use, and the enterprise reps that replaced any dropped global rep.
 * @throws Never.
 */
export function applyEnterpriseRepPrecedence<T extends Pick<HotelChainRep, "org_id">>(
  chainReps: T[],
  enterpriseReps: EnterpriseRepPreferences
): EnterpriseRepPrecedence<T> {
  if (enterpriseReps.emails.length === 0 || enterpriseReps.includeGlobalChainRep) {
    return { chainReps, replacementRepEmails: [] };
  }
  const kept = chainReps.filter((rep) => rep.org_id !== GLOBAL_ORG_ID);
  return { chainReps: kept, replacementRepEmails: kept.length < chainReps.length ? enterpriseReps.emails : [] };
}
